import React, { forwardRef, useEffect, useState } from "react";
import MaterialTable from "material-table";
import { ToastContainer, toast } from "react-toastify";
import {
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@material-ui/icons";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@material-ui/core";
import { Container, Button, Card, Row, Col } from "react-bootstrap";
import axios from "../Components/Axios/Axios";
// Custom Components Import
import Loader from "../Components/Spinner/Spinner";
import Sidebar from "../Components/Sidebar/MainSidebar/Sidebar";
import requests from "../Components/Requests/Requests";

// ---- Rows-per-page choices (kept in sync with the backend's
// ALLOWED_PAGE_SIZES whitelist in iosdata) ----
const PAGE_SIZE_OPTIONS = [30, 60, 90, 120];

// pack is multi-select -> array in state, comma separated in API
const EMPTY_FILTERS = {
  country: "",
  pack: [],
  status: "",
  fromDate: "",
  toDate: "",
};

// Builds the query params object. Arrays are sent as "a,b,c" (backend splits on comma)
const buildParams = (f) => ({
  country: f.country,
  pack: f.pack.join(","),
  status: f.status,
  fromDate: f.fromDate,
  toDate: f.toDate,
});

function AllIosData() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0); // MaterialTable uses 0-based page indexing
  const [pageSize, setPageSize] = useState(30); // rows per page, one of PAGE_SIZE_OPTIONS
  const [totalCount, setTotalCount] = useState(0);

  // ---- Export all state ----
  const [exportingAll, setExportingAll] = useState(false);
  const [summary, setSummary] = useState({
    totalSubs: 0,
    active: 0,
    expired: 0,
    cancelled: 0,
    packs: [],
  });

  // ---- Filter state ----
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  // ---- Filter dropdown options (from API) ----
  const [packOptions, setPackOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  const tableIcons = {
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => (
      <ChevronRight {...props} ref={ref} />
    )),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => (
      <ChevronLeft {...props} ref={ref} />
    )),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => (
      <ArrowDownward {...props} ref={ref} />
    )),
    ThirdStateCheck: forwardRef((props, ref) => (
      <Remove {...props} ref={ref} />
    )),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  };

  useEffect(() => {
    fetchData(page + 1, filters);
  }, [page, pageSize]);

  // ---- Fetch data, accepts filters ----
  const fetchData = async (currentPage, currentFilters = filters) => {
    setLoading(true);
    try {
      const response = await axios.get(requests.iosdata, {
        params: {
          page: currentPage,
          pageSize: pageSize,
          ...buildParams(currentFilters),
        },
      });
      if (response.data.Success === 1) {
        setData(response.data.data);
        setTotalCount(response.data.totalCount);

        if (response.data.summary) {
          setSummary(response.data.summary);
        }

        // ---- Populate filter dropdown options from API response ----
        if (response.data.filterOptions) {
          setPackOptions(response.data.filterOptions.packs || []);
          setCountryOptions(response.data.filterOptions.countries || []);
          setStatusOptions(response.data.filterOptions.statuses || []);
        }
      } else {
        toast.warning(`Something went wrong`, {
          position: "top-right",
          autoClose: 2500,
        });
      }
    } catch (err) {
      toast.warning(err.message, {
        position: "top-right",
        autoClose: 2500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // rows-per-page changed, go back to page 1
  };

  // ---- CSV helpers ----
  const csvEscape = (val) => {
    if (val === null || val === undefined) return "";
    const str = String(val);
    if (/[",\n\r]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // ---- Export ALL filtered records ----
  // Hit iosdata once WITHOUT a `page` param -- the backend treats a
  // missing page as "return every matching row, unpaginated" (see
  // isPaginated in exports.iosdata). One request, one DB query, then
  // just convert the result to CSV.
  const exportAllData = async (columnList) => {
    if (exportingAll) return;
    setExportingAll(true);

    const params = buildParams(filters);

    try {
      const response = await axios.get(requests.iosdata, { params });

      if (response.data.Success !== 1) {
        toast.warning("Export failed. Could not fetch records.", {
          position: "top-right",
          autoClose: 2500,
        });
        return;
      }

      const rows = response.data.data || [];

      if (rows.length === 0) {
        toast.info("No records match these filters.", {
          position: "top-right",
          autoClose: 2500,
        });
        return;
      }

      // Only columns that map to real data get exported; action/button
      // columns (if any) are skipped via exportSkip.
      // NOTE: the "#" column (field "id") is skipped since rows don't have an `id`
      // field; subscriptionId is exported instead.
      const exportableCols = (columnList || []).filter(
        (col) => col.field && col.field !== "id" && !col.exportSkip,
      );
      const headerLine = exportableCols
        .map((col) => csvEscape(col.title))
        .join(",");

      const lines = [headerLine];
      for (const row of rows) {
        const line = exportableCols
          .map((col) => csvEscape(row[col.field]))
          .join(",");
        lines.push(line);
      }
      const totalRows = rows.length;

      // single join, BOM so Excel reads UTF-8 correctly
      const blob = new Blob(["﻿" + lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `ios_data_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${totalRows} records`, {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (err) {
      toast.warning(err.message, {
        position: "top-right",
        autoClose: 2500,
      });
    } finally {
      setExportingAll(false);
    }
  };

  // ---- Filter handlers ----
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setPage(0);
    fetchData(1, filters);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(0);
    fetchData(1, EMPTY_FILTERS);
  };

  // ---- Reusable multi-select dropdown (checkbox list) ----
  const renderMultiSelect = (label, field, options, minWidth) => (
    <FormControl style={{ minWidth }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={filters[field]}
        onChange={(e) => handleFilterChange(field, e.target.value)}
        renderValue={(selected) => selected.join(", ")}
        MenuProps={{ getContentAnchorEl: null }}
      >
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            <Checkbox checked={filters[field].indexOf(opt) > -1} />
            <ListItemText primary={opt} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // one card per pack, packs come from the API (product_id)
  const statCards = [
    { label: "Total Subs", value: summary.totalSubs },
    { label: "Active", value: summary.active },
    { label: "Expired", value: summary.expired },
    { label: "Cancelled", value: summary.cancelled },
    ...(summary.packs || []).map((p) => ({ label: p.pack, value: p.total })),
  ];

  const cellBorder = { borderRight: "1px solid #e0e0e0" };

  return (
    <>
      <Sidebar />
      <Container className="content">
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
        />
        <Card className="card">
          <Card.Header className="heading">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              className="inside-header"
            >
              <span>IOS DATA</span>
              <span style={{ fontSize: "14px", fontWeight: "normal" }}>
                Total Results: {totalCount}
              </span>
            </div>
          </Card.Header>
          <Card.Body className="body">
            {/* ---- Summary Cards ---- */}
            <Row style={{ marginBottom: "15px" }}>
              {statCards.map((s, i) => (
                <Col
                  key={i}
                  xs={6}
                  sm={4}
                  md={3}
                  lg={2}
                  style={{ marginBottom: "10px" }}
                >
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      padding: "10px",
                      textAlign: "center",
                      background: "#f9f9f9",
                    }}
                  >
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {s.label}
                    </div>
                    <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                      {s.value ?? 0}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            {/* ---- Filter Bar ---- */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                marginBottom: "15px",
                alignItems: "center",
              }}
            >
              <TextField
                select
                label="Country"
                SelectProps={{ native: true }}
                value={filters.country}
                onChange={(e) => handleFilterChange("country", e.target.value)}
                style={{ minWidth: 120 }}
              >
                <option value=""></option>
                {countryOptions.map((c, i) => (
                  <option key={i} value={c}>
                    {c}
                  </option>
                ))}
              </TextField>

              {/* Multi-select: Pack */}
              {renderMultiSelect("Pack", "pack", packOptions, 180)}

              <TextField
                select
                label="Status"
                SelectProps={{ native: true }}
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                style={{ minWidth: 120 }}
              >
                <option value=""></option>
                {statusOptions.map((st, i) => (
                  <option key={i} value={st}>
                    {st}
                  </option>
                ))}
              </TextField>

              <TextField
                label="From Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.fromDate}
                onChange={(e) => handleFilterChange("fromDate", e.target.value)}
              />

              <TextField
                label="To Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.toDate}
                onChange={(e) => handleFilterChange("toDate", e.target.value)}
              />

              <Button variant="primary" size="sm" onClick={applyFilters}>
                Apply Filters
              </Button>
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear
              </Button>

              {exportingAll && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "13px",
                  }}
                >
                  <span className="mini-spinner" />
                  Exporting...
                </span>
              )}
            </div>

            {loading ? (
              <Loader margin={"auto"} />
            ) : (
              <MaterialTable
                data={data}
                icons={tableIcons}
                columns={[
                  {
                    title: "#",
                    field: "id",
                    render: (rowData) => rowData.tableData.id + 1,
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                      wordBreak: "break-all",
                    },
                  },
                  {
                    title: "Subscription ID",
                    field: "subscriptionId",
                    cellStyle: cellBorder,
                  },
                  {
                    title: "User ID",
                    field: "userId",
                    cellStyle: cellBorder,
                  },
                  {
                    title: "Original TX",
                    field: "orderID",
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                      wordBreak: "break-all",
                    },
                  },
                  {
                    title: "User Name",
                    field: "userName",
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                      wordBreak: "break-all",
                    },
                  },
                  {
                    title: "Email",
                    field: "userEmail",
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                      wordBreak: "break-all",
                    },
                  },
                  {
                    title: "Pack",
                    field: "pack",
                    cellStyle: cellBorder,
                  },
                  {
                    title: "Subscription Status",
                    field: "subscriptionStatus",
                    cellStyle: cellBorder,
                  },
                  {
                    title: "Entitlement Code",
                    field: "entitlementCode",
                    cellStyle: cellBorder,
                  },
                  {
                    title: "Country",
                    field: "country",
                    render: (rowData) => rowData.country || "-",
                    cellStyle: cellBorder,
                  },
                  {
                    title: "Purchase Date",
                    field: "formatted_date",
                    render: (rowData) => rowData.formatted_date || "-",
                    cellStyle: cellBorder,
                  },
                  {
                    title: "Expiry Date",
                    field: "expiryDate",
                    render: (rowData) => rowData.expiryDate || "-",
                    cellStyle: cellBorder,
                  },
                  {
                    title: "Cancellation Date",
                    field: "cancellationDate",
                    render: (rowData) => rowData.cancellationDate || "-",
                    cellStyle: cellBorder,
                  },
                  {
                    title: "Auto Renewing",
                    field: "autoRenewing",
                    // autorenew can be NULL / 0, so check for null only
                    render: (rowData) =>
                      rowData.autoRenewing === null ||
                      rowData.autoRenewing === undefined
                        ? "-"
                        : String(rowData.autoRenewing),
                    cellStyle: cellBorder,
                  },
                ]}
                options={{
                  pageSize: pageSize,
                  paginationType: "normal",
                  showTitle: false,
                  pagination: true,
                  pageSizeOptions: PAGE_SIZE_OPTIONS,
                  tableLayout: "auto",
                  search: false,
                  exportButton: true,
                  exportAllData: true,
                  // Override the built-in CSV export: renderData is only the
                  // current page, so we refetch every filtered record instead.
                  exportCsv: (columnList, renderData) => {
                    exportAllData(columnList);
                  },
                  rowStyle: (rowData, index) => {
                    return {
                      backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
                    };
                  },
                }}
                totalCount={totalCount}
                page={page}
                onChangePage={(newPage) => handlePageChange(newPage)}
                onChangeRowsPerPage={(newPageSize) =>
                  handlePageSizeChange(newPageSize)
                }
                localization={{
                  pagination: {
                    labelRowsSelect: "rows",
                    labelDisplayedRows: "{from}-{to} of {count}",
                  },
                }}
              />
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}

export default AllIosData;